import { useState } from 'react'
import { X, ArrowRight } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'

interface SmartNotification {
  type: 'new_path' | 'suggested_path' | 'continue' | 'abandoned' | 'milestone' | 'discovery'
  priority: number
  data: {
    pathId?: string
    pathTitle?: string
    topic?: string
    articleCount?: number
    daysInactive?: number
    progress?: number
    message: string
    actionLabel: string
    actionUrl: string
  }
}

interface SmartNotificationBannerProps {
  notification: SmartNotification
  onDismiss: () => void
}

export default function SmartNotificationBanner({ 
  notification, 
  onDismiss 
}: SmartNotificationBannerProps) {
  const [isVisible, setIsVisible] = useState(true)
  const navigate = useNavigate()

  const handleDismiss = () => {
    setIsVisible(false)
    onDismiss()
  }

  const handleAction = () => {
    navigate(notification.data.actionUrl)
  }

  if (!isVisible) return null

  // Get type-specific accent color
  const getAccentColor = () => {
    switch (notification.type) {
      case 'new_path':
        return 'from-green-500 via-emerald-500 to-teal-500'
      case 'suggested_path':
        return 'from-purple-500 via-violet-500 to-indigo-500'
      case 'continue':
        return 'from-blue-500 via-cyan-500 to-sky-500'
      case 'abandoned':
        return 'from-orange-500 via-amber-500 to-yellow-500'
      case 'discovery':
        return 'from-teal-500 via-cyan-500 to-blue-500'
      default:
        return 'from-blue-500 via-purple-500 to-pink-500'
    }
  }

  const accentColor = getAccentColor()

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 100 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="group relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 shadow-lg dark:shadow-gray-900/50 transition-all duration-200"
      >
        
        <div className="p-4">
          {/* Close button - top right corner */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          <div 
            onClick={handleAction}
            className="cursor-pointer flex items-center justify-between pr-6"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {notification.data.message}
                </p>
              </div>
            </div>

            {/* Action button - right side */}
            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors flex-shrink-0 ml-3">
              <span className="text-xs font-medium whitespace-nowrap">
                {notification.data.actionLabel}
              </span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
